import { proxy } from 'valtio/vanilla'

/**
 * Mouse tracking state for cursor management
 */
export interface MouseState {
  /** Current mouse X position */
  mouseX: number
  /** Current mouse Y position */
  mouseY: number
  /** Timestamp of last mouse activity */
  lastMouseActivity: number
  /** Whether mouse is over the game screen */
  isMouseOverGame: boolean
  /** Whether cursor is currently hidden */
  isCursorHidden: boolean
  /** Inactivity timeout in milliseconds */
  cursorHideDelay: number
}

/**
 * Creates the initial mouse state
 */
function createInitialMouseState(): MouseState {
  return {
    mouseX: 0,
    mouseY: 0,
    lastMouseActivity: Date.now(),
    isMouseOverGame: false,
    isCursorHidden: false,
    cursorHideDelay: 2000, // 2 seconds
  }
}

/**
 * The mouse store
 */
export const mouseStore = proxy<MouseState>(createInitialMouseState())

/**
 * Update mouse position and activity timestamp
 */
export function updateMouseActivity(x: number, y: number): void {
  mouseStore.mouseX = x
  mouseStore.mouseY = y
  mouseStore.lastMouseActivity = Date.now()

  // Show cursor immediately on mouse activity
  if (mouseStore.isCursorHidden) {
    mouseStore.isCursorHidden = false
  }
}

/**
 * Update mouse over game state
 */
export function setMouseOverGame(isOver: boolean): void {
  mouseStore.isMouseOverGame = isOver

  // Show cursor immediately when leaving game area
  if (!isOver && mouseStore.isCursorHidden) {
    mouseStore.isCursorHidden = false
  }
}

/**
 * Check if mouse should be hidden due to inactivity
 */
export function checkMouseInactivity(): void {
  const now = Date.now()
  const inactiveTime = now - mouseStore.lastMouseActivity

  // Note: This function now only checks mouse inactivity
  // The activeGame check should be done by the caller
  const shouldHide =
    mouseStore.isMouseOverGame &&
    inactiveTime > mouseStore.cursorHideDelay &&
    !mouseStore.isCursorHidden

  if (shouldHide) {
    mouseStore.isCursorHidden = true
  }
}

/**
 * Reset the mouse store to initial state (useful for testing)
 */
export function resetMouseStore(): void {
  const initial = createInitialMouseState()
  mouseStore.mouseX = initial.mouseX
  mouseStore.mouseY = initial.mouseY
  mouseStore.lastMouseActivity = initial.lastMouseActivity
  mouseStore.isMouseOverGame = initial.isMouseOverGame
  mouseStore.isCursorHidden = initial.isCursorHidden
  mouseStore.cursorHideDelay = initial.cursorHideDelay
}
